import { useEffect, useState } from "react";
import { Download, X, RefreshCw } from "lucide-react";
import { onUpdateAvailable } from "../pwa/serviceWorkerRegistration";

const DISMISS_KEY = "ta247_pwa_install_dismissed_v1";

/**
 * Lightweight, non-blocking PWA install + update banner.
 *
 * - Listens for `beforeinstallprompt` (Chrome/Edge/Android) and stores the
 *   event so the user can install on demand.
 * - Listens for service-worker updates and offers "Update to latest" when a
 *   new version is waiting.
 *
 * Dismissal is sticky (localStorage) so we never nag.
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "true"; } catch { return false; }
  });
  const [updateFn, setUpdateFn] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const off = onUpdateAvailable((apply) => setUpdateFn(() => apply));
    return off;
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "true"); } catch { /* ignore */ }
  };

  if (updateFn) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] bg-orange-500 text-white shadow-lg rounded-xl px-4 py-3 flex items-center justify-between gap-3"
        data-testid="pwa-update-banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          <RefreshCw className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm font-medium">A new version is available</p>
        </div>
        <button
          onClick={updateFn}
          className="text-xs font-semibold bg-white text-orange-600 rounded-md px-3 py-1.5 hover:bg-orange-50"
          data-testid="pwa-apply-update-btn"
        >
          Reload
        </button>
      </div>
    );
  }

  if (dismissed || !deferred) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-2rem)] bg-slate-900 text-white shadow-xl rounded-xl px-4 py-3 flex items-center justify-between gap-3"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Download className="w-4 h-4 text-orange-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">Install Transport 247</p>
          <p className="text-[11px] text-slate-300 truncate">Faster access from your home screen</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={install}
          className="text-xs font-semibold bg-orange-500 text-white rounded-md px-3 py-1.5 hover:bg-orange-600"
          data-testid="pwa-install-btn"
        >
          Install
        </button>
        <button
          onClick={dismiss}
          className="text-slate-300 hover:text-white p-1"
          aria-label="Dismiss"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
